import { put, call } from 'redux-saga/effects';
import {
  showAlert,
  getHealthCheckReport as getHealthCheckReportAction,
  getLastHealthCheckForLocation as getLastHealthCheckForLocationAction,
} from 'actions/index';
import { ActionTypes } from 'constants/index';

const SYSTEM_CHECK_KEYS_BY_DEVICE = {
  'water-filter': [
    'inlet-pressure-test',
    'outlet-pressure-test',
    'device-connection-status',
    'normal-flow-operation',
    'bypass-during-normal-flow-check',
  ],
  'reverse-osmosis': [
    'inlet-pressure',
    'tank-pressure-detection',
    'system-pressure-detection',
    'device-connection-status',
    'ro-feed-flow-detection',
    'permeate-production-check',
    'inlet-product-bypass-flow-detection',
    'inlet-product-flow-verification',
  ],
  'water-softener': ['brine-level-detection', 'device-connection-status'],
};

const getHealthCheckAlertId = reportId => `health-check-${reportId}`;

const getResponseData = data => data?.data || data || {};

const getErrorMessage = (data, fallback) =>
  data?.errors?.message || data?.message || data?.data?.errors?.message || fallback;

const getCheckStatus = (data, checkKey) => {
  const checks = data?.checks || {};
  const check = checks[checkKey] || checks[Object.keys(checks)[0]];
  return check?.status || data?.overallStatus || data?.overall_status;
};

function* refreshHealthCheckViews(locationId, reportId) {
  if (locationId) {
    yield put(getLastHealthCheckForLocationAction(locationId));
  }
  if (reportId) {
    yield put(getHealthCheckReportAction(reportId));
  }
}

export function* startHealthCheck(api, action) {
  const { location_id, device_type, report_id: existingReportId } = action.payload;
  const checkKeys = SYSTEM_CHECK_KEYS_BY_DEVICE[device_type] || [];

  if (!location_id || !device_type || !checkKeys.length) {
    const message = 'Unable to run health check for this location';
    yield put({
      type: ActionTypes.START_HEALTH_CHECK_FAILURE,
      payload: message,
    });
    yield put(showAlert(message, { variant: 'danger', timeout: 9 }));
    return;
  }

  let reportId;
  let attemptNumber;
  let hasFailedCheck = false;

  try {
    const reportResponse = existingReportId
      ? yield call(api.startHealthCheckAttempt, existingReportId)
      : yield call(api.startHealthCheckReport, {
          location_id,
          device_type,
          check_type: 'system',
          execution_source: 'manual',
          status: 'pending',
        });
    const { status: reportStatus, data: reportRawData } = reportResponse;
    const reportData = getResponseData(reportRawData);

    if (reportStatus !== 200 || !reportData.report_id || reportData.attempt_number == null) {
      const message = getErrorMessage(
        reportRawData,
        existingReportId
          ? 'Error starting health check attempt'
          : 'Error starting health check report',
      );
      yield put({
        type: ActionTypes.START_HEALTH_CHECK_FAILURE,
        payload: message,
      });
      if (reportStatus !== 500 && reportStatus !== 401) {
        yield put(showAlert(message, { variant: 'danger', timeout: 9 }));
      }
      return;
    }

    reportId = reportData.report_id;
    attemptNumber = reportData.attempt_number;

    yield put(
      showAlert('Health check in progress', {
        id: getHealthCheckAlertId(reportId),
        icon: 'spinner',
        loading: true,
        timeout: 0,
        variant: 'info',
      }),
    );

    for (const checkKey of checkKeys) {
      const checkResponse = yield call(api.startHealthCheck, {
        location_id,
        device_type,
        check_type: 'system',
        value_type: checkKey,
        report_id: reportId,
        attempt_number: attemptNumber,
        execution_source: 'manual',
      });
      const { status: checkStatus, data: checkRawData } = checkResponse;
      const checkData = getResponseData(checkRawData);

      if (checkStatus !== 200) {
        const message = getErrorMessage(checkRawData, 'Error running health check');
        yield put({
          type: ActionTypes.START_HEALTH_CHECK_FAILURE,
          payload: message,
        });
        if (reportId || (checkStatus !== 500 && checkStatus !== 401)) {
          yield put(
            showAlert(message, {
              action: reportId
                ? {
                    label: 'View details',
                    to: `/health-checks/detail/${reportId}`,
                  }
                : undefined,
              id: reportId ? getHealthCheckAlertId(reportId) : undefined,
              timeout: 9,
              variant: 'danger',
            }),
          );
        }
        yield call(refreshHealthCheckViews, location_id, reportId);
        return;
      }

      if (getCheckStatus(checkData, checkKey) === 'failed') {
        hasFailedCheck = true;
      }
    }

    yield put({
      type: ActionTypes.START_HEALTH_CHECK_SUCCESS,
      payload: { report_id: reportId, attempt_number: attemptNumber },
    });
    yield call(refreshHealthCheckViews, location_id, reportId);
    yield put(
      showAlert(
        hasFailedCheck ? 'Health check completed with failures' : 'Health check completed',
        {
          action: {
            label: 'View details',
            to: `/health-checks/detail/${reportId}`,
          },
          id: getHealthCheckAlertId(reportId),
          variant: hasFailedCheck ? 'warning' : 'success',
          timeout: 12,
        },
      ),
    );
  } catch (error) {
    const message = error?.message || 'Connection error while running health check';
    yield put({
      type: ActionTypes.START_HEALTH_CHECK_FAILURE,
      payload: message,
    });
    yield put(
      showAlert(message, {
        action: reportId
          ? {
              label: 'View details',
              to: `/health-checks/detail/${reportId}`,
            }
          : undefined,
        id: reportId ? getHealthCheckAlertId(reportId) : undefined,
        variant: 'danger',
        timeout: 9,
      }),
    );
    yield call(refreshHealthCheckViews, location_id, reportId);
  }
}

export function* getHealthChecks(api, action) {
  const response = yield call(api.getHealthChecks, action.payload);
  const { status, data } = response;

  if (status === 200) {
    const { data: list, pagination } = data;
    yield put({
      type: ActionTypes.GET_HEALTH_CHECKS_SUCCESS,
      payload: { list: list || [], pagination: pagination || {} },
    });
  } else {
    const { errors } = data || {};
    yield put({
      type: ActionTypes.GET_HEALTH_CHECKS_FAILURE,
    });
    if (status !== 500 && status !== 401) {
      yield put(
        showAlert(errors?.message || 'Error loading health checks', {
          variant: 'danger',
          timeout: 9,
        }),
      );
    }
  }
}

export function* getHealthCheckMetrics(api, action) {
  const response = yield call(api.getHealthCheckMetrics, action.payload);
  const { status, data } = response;

  if (status === 200) {
    yield put({
      type: ActionTypes.GET_HEALTH_CHECK_METRICS_SUCCESS,
      payload: data?.data || {},
    });
  } else {
    const { errors } = data || {};
    yield put({
      type: ActionTypes.GET_HEALTH_CHECK_METRICS_FAILURE,
    });
    if (status !== 500 && status !== 401) {
      yield put(
        showAlert(errors?.message || 'Error loading health check metrics', {
          variant: 'danger',
          timeout: 9,
        }),
      );
    }
  }
}

export function* getHealthCheckReport(api, action) {
  const { reportId, meta = {} } = action.payload;
  const response = yield call(api.getHealthCheckReport, reportId);
  const { status, data } = response;

  if (status === 200) {
    yield put({
      type: ActionTypes.GET_HEALTH_CHECK_REPORT_SUCCESS,
      payload: { report: data?.data || null, reportId, meta },
    });
  } else {
    const { errors } = data || {};
    yield put({
      type: ActionTypes.GET_HEALTH_CHECK_REPORT_FAILURE,
      payload: { error: errors?.message || 'Error loading report', reportId, meta },
    });
    if (status !== 500 && status !== 401) {
      yield put(
        showAlert(errors?.message || 'Error loading health check report', {
          variant: 'danger',
          timeout: 9,
        }),
      );
    }
  }
}

export function* getLastHealthCheckForLocation(api, action) {
  try {
    const { locationId } = action.payload;
    const response = yield call(api.getLastHealthCheckForLocation, locationId);
    const { status, data } = response;

    if (status === 200) {
      yield put({
        type: ActionTypes.GET_LAST_HEALTH_CHECK_FOR_LOCATION_SUCCESS,
        payload: data?.data || null,
      });
    } else {
      const { errors } = data || {};
      yield put({
        type: ActionTypes.GET_LAST_HEALTH_CHECK_FOR_LOCATION_FAILURE,
        payload: errors?.message || 'Error loading last health check',
      });
      // Do not show global alert for 404 (no health check for location)
      if (status !== 404 && status !== 500 && status !== 401) {
        yield put(
          showAlert(errors?.message || 'Error loading last health check', {
            variant: 'danger',
            timeout: 9,
          }),
        );
      }
    }
  } catch (error) {
    yield put({
      type: ActionTypes.GET_LAST_HEALTH_CHECK_FOR_LOCATION_FAILURE,
      payload: error?.message || 'Error loading last health check',
    });
  }
}
